/**
 * OS-level notification surface for when the browser tab is hidden
 * (different tab, minimized, behind another window). Sonner toasts only
 * help when the tab is focused; this fills that gap.
 *
 * Permission must be requested from a user gesture; the "Enable sound"
 * button in NotificationCenter co-requests this so one click covers both.
 */

export type DesktopNotificationPermission = 'granted' | 'denied' | 'default' | 'unsupported';

export function isDesktopNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getDesktopNotificationPermission(): DesktopNotificationPermission {
  if (!isDesktopNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

export async function requestDesktopNotificationPermission(): Promise<DesktopNotificationPermission> {
  if (!isDesktopNotificationSupported()) return 'unsupported';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'default';
  }
}

interface ShowOptions {
  title: string;
  body: string;
  onClick?: () => void;
  /** Used as the Notification `tag` so re-fires for the same order replace earlier ones. */
  tag?: string;
}

/**
 * Show an OS notification iff the tab is hidden and permission is granted.
 * No-op when the tab is focused — sonner toast handles that case in
 * useNotificationStream.
 */
export function showDesktopNotification({ title, body, onClick, tag }: ShowOptions): void {
  if (!isDesktopNotificationSupported()) return;
  if (Notification.permission !== 'granted') return;
  if (typeof document !== 'undefined' && !document.hidden) return;

  try {
    const n = new Notification(title, {
      body,
      tag,
      icon: '/favicon.ico',
      requireInteraction: false,
    });
    n.onclick = () => {
      try {
        window.focus();
      } catch {
        // some browsers reject focus() outside a gesture — best-effort
      }
      onClick?.();
      n.close();
    };
  } catch {
    // Notification constructor can throw on some platforms (mobile Safari etc.)
  }
}
